import React, { createContext, useContext, useMemo } from 'react';

import type { SerializableRecord, SubscriptionAdapter } from './types';

/**
 * Subscription adapter context type.
 * Used for both provider props and context value since they have the same structure.
 */
export type SubscriptionAdapterContextType = {
	/**
	 * The subscription adapter implementation.
	 * Products should provide either a Relay or Apollo adapter based on their GraphQL client.
	 */
	subscriptionAdapter?: SubscriptionAdapter<SerializableRecord>;
};

/**
 * Alias for SubscriptionAdapterContextType for use as provider props.
 * They have the same structure, so we reuse the same type.
 */
export type SubscriptionAdapterProviderProps = SubscriptionAdapterContextType;

const SubscriptionAdapterContext = createContext<SubscriptionAdapterContextType>({
	subscriptionAdapter: undefined,
});

export const StableSubscriptionAdapterProvider = ({
	subscriptionAdapter,
	children,
}: SubscriptionAdapterProviderProps & { children?: React.ReactNode }) => {
	const memoizedValue = useMemo(
		() => ({
			subscriptionAdapter,
		}),
		[subscriptionAdapter],
	);

	return (
		<SubscriptionAdapterContext.Provider value={memoizedValue}>
			{children}
		</SubscriptionAdapterContext.Provider>
	);
};

export const useSubscriptionAdapter = (): SubscriptionAdapterContextType => {
	return useContext(SubscriptionAdapterContext);
};
